import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Switch } from 'antd';

import { createUploadFileObj } from '@/core/domain/archive/Archive/Archive.utils';

import {
  useActiveInactivePart,
  useDeletePart,
  usePart,
} from '@/core/domain/operational/Parts/Parts.hooks';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import { useDefaultDeleteModal, useWindowSize } from '@/hooks';

import { PageProps } from '@/types/PageProps';

import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import PartForm, {
  PartFormData,
} from '@/components/Operational/Inventory/Parts/PartForm';
import { DeleteButton, SaveButton } from '@/components/_common/Buttons';

import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useInventoryPartFormContext } from '../../InventoryPartsProvider';
import PartFooter from '@/components/Operational/Inventory/Parts/PartFooter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

const basePath = '/app/operational/inventory/parts';

function PartGeneralInfoPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<PartFormData>();
  const { setParamLabel } = useBreadcrumbs();
  const { handleDelete } = useDefaultDeleteModal('parts');
  const history = useHistory();
  const [activeLoad, setActiveLoad] = useState<boolean>(true);
  const { isMobile } = useWindowSize();

  const { active, setActive, contextForm, disabled, isLoading, partId } =
    useInventoryPartFormContext();

  const { data, isFetching } = usePart(+partId);

  const deleteMutation = useDeletePart();
  const activeInactive = useActiveInactivePart();

  useEffect(() => {
    if (contextForm.generalParts) {
      form.setFieldsValue(contextForm.generalParts);
    } else if (data) {
      setActive(data.active);
      const { imageUrl, ...rest } = data;

      const img = createUploadFileObj(imageUrl);

      const formObj: PartFormData = {
        ...rest,
        manufacturerId: rest?.manufacturer?.id,
        subGroupId: rest?.subGroup?.id,
        imageUrl: img ? [img] : [],
      };

      form.setFieldsValue(formObj);

      setParamLabel(':partId', data.name);
    }
  }, [data, contextForm.generalParts]);

  useEffect(() => {
    if (!isFetching) {
      setActiveLoad(false);
    }
  }, [isFetching]);

  const submitForm = async () => {
    try {
      await form.validateFields();
      form.submit();
    } catch {}
  };

  const handleActiveInactive = async () => {
    setActiveLoad(true);
    try {
      await activeInactive.mutateAsync(+partId).then(() => setActive(!active));
    } catch {
    } finally {
      setActiveLoad(false);
    }
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: 'parts.details' })} />

      <DefaultRouteTabs
        route={route.parent}
        permissions={route.permissions}
        baseKey={partId}
        basePath={`${basePath}/${partId}`}
        extra={
          <SpaceForButton flexDirection={isMobile ? 'column-reverse' : 'row'}>
            <Switch
              checkedChildren={<FormattedMessage id="commons.entity.active" />}
              unCheckedChildren={
                <FormattedMessage id="commons.entity.inactive" />
              }
              checked={active}
              loading={activeLoad}
              onChange={handleActiveInactive}
              disabled={!hasPermissions([Authorities.REGISTER_PART_EDIT])}
            />
            <DeleteButton
              block
              onClick={() =>
                handleDelete(
                  () => deleteMutation.mutateAsync(Number(partId)),
                  async () => history.replace(basePath),
                )
              }
              disabled={!hasPermissions([Authorities.REGISTER_PART_DELETE])}
            >
              <FormattedMessage id="commons.entity.remove" />
            </DeleteButton>
            <SaveButton
              block
              type="primary"
              onClick={submitForm}
              loading={isLoading}
              disabled={
                disabled || !hasPermissions([Authorities.REGISTER_PART_EDIT])
              }
            >
              <FormattedMessage id="commons.entity.save" />
            </SaveButton>
          </SpaceForButton>
        }
      >
        <PartForm isLoading={isFetching} form={form} partDetail={data} />

        <PartFooter partId={Number(partId)} disabled={disabled} />
      </DefaultRouteTabs>
    </>
  );
}

export default PartGeneralInfoPage;
